import api from 'api';
import Button from 'components/Button';
import React from 'react';
import qs from 'query-string';
import {useTranslation} from 'react-i18next';
import {DialogActions, Grid, Typography} from '@mui/material';
import {useGetApi} from 'common/hooks/useGetApi';
import {IJobHistory} from 'types/JobHistory';
import {DialogContent} from 'components/Dialog';
import {JobStatus} from 'types/diplomat/enums/JobStatus';
import {JobCompletionStatus} from 'types/diplomat/enums/JobCompletionStatus';
import AsyncContainer from 'components/AsyncContainer';
import Panel from 'components/Panel';
import {FileStatus} from 'types/diplomat/enums/FileStatus';
import {FileCompletionStatus} from 'types/diplomat/enums/FileCompletionStatus';
import {timeTo} from 'common/dateUtils';
import {usePostApiCallback} from 'common/hooks/usePostApiCallback';
import {IJobSequence} from 'types/diplomat/job/JobSequence';
import useInterval from 'common/hooks/useInterval';
import {downloadTextFile} from 'common/downloadFile';
import {IErrorResponse} from 'types/diplomat/ErrorResponse';
import prettyBytes from 'pretty-bytes';
import {mbps} from 'common/utils';
import dayjs from 'dayjs';
import {
   DataGridPro,
   GridColDef,
   GridValueFormatterParams,
   GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import {FileHistoryNames, IFileHistory} from 'types/FileHistory';

const useGetJobHistoryRepeatedly = (
   transactionId: string,
   jobSequence?: string,
   runNowError?: any
) => {
   const {request: getJobHistory, state: jobHistory = {} as IJobHistory} = useGetApi<IJobHistory>(
      api.jobhistory,
      {persistState: true}
   );

   const isCompleted = !!runNowError || jobHistory?.jobStatus?.id === JobStatus.enum.Complete;
   const seq = jobSequence;
   // const delay = !isCompleted && seq ? 5 * 1000 : null;
   const [delay, setDelay] = React.useState<null | number>(1000);

   React.useEffect(() => {
      if (isCompleted) {
         setDelay(null);
      }
   }, [isCompleted, delay]);

   useInterval(() => {
      getJobHistory(`/${seq}`);
      if (!isCompleted && seq) {
         setDelay(delay && delay < 5000 ? delay + 1000 : delay);
      }
   }, delay);

   const {request: getJobSummary, state: jobSummary} = useGetApi<IJobSequence>(
      `${api.jobSummary}/${transactionId}`
   );

   // Clear
   React.useEffect(() => {
      const isCompleted = jobHistory?.jobStatus?.id === JobStatus.enum.Complete;
      // console.debug(isCompleted, destroyDemonRef.current);
      if (isCompleted) {
         getJobSummary();
      }
   }, [jobHistory?.jobStatus?.id, getJobSummary]);

   // jobHistory.bytesTransferred = random(4, 382179071);
   // jobHistory.bytesExpected = 658680275;

   return {
      jobHistory,
      jobSummary,
   };
};

const cols: GridColDef[] = [
   {
      field: FileHistoryNames.source,
      headerName: 'Name',
      flex: 3,
      // renderCell: (params: GridCellParams) => {
      //    return (
      //       <TransactionStatusContextWrapper transactionStatus={params.row as ITransactionStatus}>
      //          <JobStateStatusIcon
      //             state={params.row.state}
      //             jobStatus={get(params.row, 'jobHistory.jobStatus.id')}
      //          />
      //          {params.value}
      //       </TransactionStatusContextWrapper>
      //    );
      // },
   },
   {
      field: FileHistoryNames.size,
      headerName: 'Size',
      flex: 1,
      // headerClassName: classes.header,
      // cellClassName: classes.centerCell,
      valueFormatter: (params) =>
         Number(params.value) > -1 ? prettyBytes(Number(params.value)).toUpperCase() : '',
   },
   {
      field: FileHistoryNames.status,
      headerName: 'Status',
      flex: 1,
      valueGetter: (params: GridValueGetterParams<IFileHistory>) => {
         const fh = params.row as IFileHistory;
         return (
            (FileStatus[fh.status.id] || fh.status.id) +
            (fh.status.subStatus ? `(${fh.status.subStatus})` : '')
         );
      },
   },
   {
      field: FileHistoryNames.completionStatus,
      headerName: 'Completion Status',
      flex: 1,
      // headerClassName: classes.header,
      // cellClassName: classes.centerCell,
      valueFormatter: (params) =>
         FileCompletionStatus[params.value as keyof typeof FileCompletionStatus],
   },
   {
      field: FileHistoryNames.startTime,
      headerName: 'Rate',
      flex: 1,
      valueGetter: (params) => {
         const fh = params.row as IFileHistory;
         if (fh.endTime === null) return '';
         return fh.endTime !== fh.startTime
            ? mbps(fh.size, dayjs(fh.endTime).diff(dayjs(fh.startTime), 'seconds'))
            : '< 1s';
      },
   },
];

type Props = {
   id: string; // Pick<INav, 'id' | 'name'>; // objectType is inside id;
   isSynchronization: boolean;
   runNowError: any;
   jobSequence: string;
   onClose: () => void;
};

export const RunNowPanel = React.memo(
   ({id: transactionOrSyncId, isSynchronization, runNowError, jobSequence, onClose}: Props) => {
      const {t} = useTranslation();

      const {jobHistory, jobSummary} = useGetJobHistoryRepeatedly(
         transactionOrSyncId,
         jobSequence,
         runNowError
      );
      const isCompleted = !!runNowError || jobHistory?.jobStatus?.id === JobStatus.enum.Complete;
      const isNotInProgress = !jobHistory?.jobStatus || isCompleted;

      const {
         request: cancelRequest,
         state: cancelState,
         loading: cancelLoading,
      } = usePostApiCallback();
      const handleCancel = React.useCallback(() => {
         let url = `${api.jobCancel}/${transactionOrSyncId}`;
         if (cancelState) {
            url = qs.stringifyUrl({url, query: {forcedCancellation: 'true'}});
         }
         cancelRequest(url);
      }, [cancelRequest, cancelState, transactionOrSyncId]);

      const handleClose = onClose;

      const startTime = React.useMemo(() => new Date(), []);
      const [timer, setTimer] = React.useState(() => timeTo(startTime));
      const updateTimer = React.useCallback(() => {
         setTimer(timeTo(startTime!));
      }, [setTimer, startTime]);

      useInterval(updateTimer, isCompleted ? null : 1000);

      React.useEffect(() => {
         if (isCompleted) {
            setTimer(timeTo(jobHistory.startTime, jobHistory.endTime));
         }
      }, [isCompleted, setTimer, jobHistory.startTime, jobHistory.endTime]);

      return (
         <>
            <AsyncContainer loading={!jobHistory}>
               <DialogContent>
                  <Grid container spacing={2}>
                     <Grid item>
                        <Typography>
                           {t('Status')}:{' '}
                           {jobHistory.running
                              ? `${JobStatus[jobHistory!.jobStatus.id]} ${
                                   jobHistory.jobStatus.subStatus
                                      ? `(${jobHistory.jobStatus.subStatus}${bytesOutput(
                                           jobHistory
                                        )})`
                                      : ''
                                }`
                              : JobCompletionStatus[jobHistory!.completionStatus]?.name}
                        </Typography>
                     </Grid>
                     <Grid item>
                        <Typography>
                           {timer} {t('(min:sec)')}
                        </Typography>
                     </Grid>
                     {!isNotInProgress ? (
                        <Grid item>
                           {isSynchronization ? (
                              <Typography>
                                 {jobHistory.filesSuccessful}{' '}
                                 {t('files processed', {count: jobHistory.filesSuccessful})}
                              </Typography>
                           ) : (
                              <Typography>
                                 {Math.trunc(100 * (Number(jobHistory!.completion) || 0))}%{' '}
                                 {t('Complete')}
                              </Typography>
                           )}
                        </Grid>
                     ) : null}
                  </Grid>
                  <Panel style={{minHeight: '20rem', height: '50rem', overflow: 'auto'}}>
                     {
                        isNotInProgress ? (
                           <Typography style={{whiteSpace: 'pre-wrap'}}>
                              {(runNowError as IErrorResponse)?.errors?.map((e) => e.message) ||
                                 jobSummary?.result ||
                                 jobSummary}
                           </Typography>
                        ) : (
                           <DataGridPro
                              // className={classes.transactionGrid}
                              density="compact"
                              rows={jobHistory?.fileHistories}
                              getRowId={(row) => row[FileHistoryNames.source]}
                              columns={cols}
                              hideFooter
                           />
                        )
                        // (
                        //    jobHistory?.fileHistories?.map((fh) =>
                        //       fh.status.id === FileStatus.enum.Queued ? null : (
                        //          <Grid container>
                        //             <Grid item xs={4}>
                        //                {fh.source}
                        //             </Grid>
                        //             {fh.size > -1 ? (
                        //                <Grid item xs={2}>
                        //                   {prettyBytes(Number(fh.size)).toUpperCase()}
                        //                </Grid>
                        //             ) : null}
                        //             <Grid item xs={2}>
                        //                {FileStatus[fh.status.id] || fh.status.id}
                        //                {fh.status.subStatus && `(${fh.status.subStatus})`}
                        //             </Grid>
                        //             {fh.completionStatus ? (
                        //                <Grid item xs={2}>
                        //                   {FileCompletionStatus[fh.completionStatus]}
                        //                </Grid>
                        //             ) : null}
                        //             {fh.endTime && fh.startTime && fh.endTime !== fh.startTime && (
                        //                <Grid item xs={2}>
                        //                   {prettyBytes(
                        //                      fh.size /
                        //                         dayjs(fh.endTime).diff(dayjs(fh.startTime), 'seconds')
                        //                   )}
                        //                   ps
                        //                </Grid>
                        //             )}
                        //          </Grid>
                        //       )
                        //    )
                        // )
                     }
                  </Panel>
               </DialogContent>
            </AsyncContainer>
            <DialogActions>
               {isNotInProgress ? (
                  <>
                     <Button
                        onClick={() =>
                           downloadTextFile(
                              jobSummary?.result || '',
                              `runnow_${transactionOrSyncId}_${jobSequence}.log`
                           )
                        }
                     >
                        {t('Save Log File')}
                     </Button>
                     <Button onClick={handleClose}>{t('Close')}</Button>
                  </>
               ) : (
                  <Button onClick={handleCancel} disabled={cancelLoading}>
                     {cancelState ? t('Terminate') : t('Cancel')}
                  </Button>
               )}
            </DialogActions>
         </>
      );
   }
);

function bytesOutput(jobHistory: IJobHistory) {
   return jobHistory.bytesTransferred && jobHistory.bytesExpected
      ? `: ${prettyBytes(Number(jobHistory.bytesTransferred)).toUpperCase()} out of ${prettyBytes(
           Number(jobHistory.bytesExpected)
        ).toUpperCase()}`
      : '';
}
